import { observer } from 'mobx-react-lite'
import { PriceTableStyled } from './style'
import TitleTabs from './TitleTabs'
import PriceTabs from './PriceTabs'
import TypeTabs from './TypeTabs'
import { useStore } from '@/store/index'
import { COIN_GATE_IDS, COIN_TRADE, HOME_PAGE_URL } from '@/constants/index'
import { useDebounceFn, useMemoizedFn, useMount, useRequest, useUpdateEffect } from 'ahooks'
import { createRef, useEffect, useMemo, useRef, useState } from 'react'
import BaseReactTable, { BaseTableRef, BaseTableColumn } from '@/components/BaseTable'
import FundingListCard from '@/blocks/Price/PriceTable/Columns/FundingListCard'
import type { BaseListProps, BasePriceItem } from 'api-price'
import useColumns from './Columns'
import { getPriceList, getWeekLine } from 'api/price'
import useWsUrl, { getId } from '@/hooks/useWsUrl'
import useWebSocket from '@/hooks/useWebSocket'
import useLangPrefix from '@/hooks/useLangPrefix'
import { getCoinNameLanguage, postFavorite } from 'api/base'
import { showNotification } from '@gate/gui'
import { eventTracker } from '@/utils/collectEvent'
import useModalLogin from '@/hooks/useModalLogin'
import dayjs from 'dayjs'
import { useRouter } from 'next/router'
import { useAdapter } from '@/components/Adapter'
import { ArrowScrollTabsV2 } from '@gate/gui-business'
import useRateChange from '@/hooks/useRateChange'
import { bigNumberDividedBy } from '@/utils/tools'

export interface PriceTableItemProps {
  onChangeTab: (tab: any, type?: string) => void
}

const PriceTable = () => {
  const { isH5 } = useAdapter()
  const { locale = 'en', query } = useRouter()
  const { isZh, langPrefix } = useLangPrefix()
  const { faitRateItem } = useRateChange()

  const { openLoginModal } = useModalLogin()

  const [selectValue, setSelectValue] = useState('utc')

  const { globalStore, baseStore } = useStore()
  const { commonData } = globalStore
  const { typeTabsList, priceTab, priceList, titleTab, setList, list, setTableRef } = baseStore

  const ref = useRef<BaseTableRef<BasePriceItem, BaseListProps>>(null)

  useUpdateEffect(() => {
    if (titleTab === HOME_PAGE_URL.Fundraising) {
      const params = ref?.current?.getParams()
      ref?.current?.fetchData(params as any, HOME_PAGE_URL.Fundraising, locale ?? 'en')
    }
  }, [locale, titleTab])

  useEffect(() => {
    setTableRef(ref.current)

    return () => {
      setTableRef(null)
    }
  }, [ref.current, titleTab])

  const BaseTable = useMemo(() => {
    return BaseReactTable<BasePriceItem, BaseListProps>()
  }, [])

  // todo ws
  const { wsUrl } = useWsUrl()

  const onMessage = useMemoizedFn(message => {
    if (message?.method === 'ticker.update' && message?.params?.[0]) {
      const id = message?.params?.[0]
      const newItem = message?.params?.[1]
      ref.current?.setData(data => ({
        ...data,
        list: data.list?.map(item => {
          if (`${item?.crypto}_${COIN_TRADE}` === id) {
            return {
              ...item,
              price: newItem?.last ?? item?.price,
              change: newItem?.change ?? item?.change,
              volume: newItem?.baseVolume ?? item?.volume,
              low_24h: newItem?.low ?? item?.low_24h,
              high_24h: newItem?.high ?? item?.high_24h,
            }
          }
          return item
        }),
      }))
    }
  })

  const { isOpen, sendMassage } = useWebSocket(wsUrl, {
    onMessage: onMessage,
  })

  //  todo ws 切换参数
  const handleSendMassage = useMemoizedFn((params: string[], method = 'ticker.set_subscription_change_timezone') => {
    if (isOpen && titleTab !== HOME_PAGE_URL.Fundraising) {
      sendMassage({
        id: getId(),
        method,
        params,
      })
    }
  })

  useEffect(() => {
    if (isOpen && titleTab !== HOME_PAGE_URL.Fundraising) {
      handleSendMassage(
        priceList?.list?.map((item: any) => `${item?.crypto?.toLocaleUpperCase()}_${COIN_TRADE}`) ?? [],
        'ticker.subscribe'
      )
    }
  }, [isOpen])

  // todo 获取折线图数据
  const {
    run: getLine,
    loading: lineLoading,
    data: lineData,
  } = useRequest(getWeekLine, {
    manual: true,
  })

  useMount(() => {
    const gate_ids = COIN_GATE_IDS(priceList?.list ?? [])
    if (gate_ids) getLine(gate_ids)
  })

  useUpdateEffect(() => {
    coinNameLocaleFunc()
  }, [locale])

  // todo 切换ws参数
  const onSelectChange = useMemoizedFn((value: string | null) => {
    setSelectValue(value ?? 'utc')
    handleSendMassage([value ?? 'utc'])
  })

  // todo 表格项
  const isCollect = useMemo(() => {
    return [HOME_PAGE_URL.Favorites, HOME_PAGE_URL.New, HOME_PAGE_URL.Market, HOME_PAGE_URL.My].includes(priceTab)
  }, [priceTab])

  const coinNameLocaleFunc = () => {
    if (titleTab === HOME_PAGE_URL.Sectors) {
      ref.current?.fetchData({ page: Number(query?.page ?? 1), pageSize: 20 }, titleTab, locale).then((res: any) => {
        const gateIds = res?.list?.map?.((i: any) => i?.gate_id)
        getCoinNameLanguage({ lang: locale, gateIds }).then(coin => {
          ref.current?.setData(data => ({
            ...data,
            list: data.list?.map(item => ({
              ...item,
              currency:
                coin?.[item?.gate_id]?.[locale ?? ''] ??
                (isZh ? (coin?.[item?.gate_id]?.zh ?? coin?.[item?.gate_id]?.en) : coin?.[item?.gate_id]?.en) ??
                item?.currency,
            })),
          }))
        })
      })
    } else {
      const gateIds = list.map(i => i?.gate_id)
      getCoinNameLanguage({ lang: locale, gateIds }).then(coin => {
        ref.current?.setData(data => ({
          ...data,
          list: data.list?.map(item => ({
            ...item,
            currency:
              coin?.[item?.gate_id]?.[locale ?? ''] ??
              (isZh ? (coin?.[item?.gate_id]?.zh ?? coin?.[item?.gate_id]?.en) : coin?.[item?.gate_id]?.en) ??
              item?.currency,
          })),
        }))
      })
    }
  }

  // todo 收藏逻辑
  const onCollect = useMemoizedFn((props: BasePriceItem) => {
    if (!commonData?.is_login) {
      openLoginModal()
      return
    }

    try {
      postFavorite({
        type: props.isFavorite === 1 ? 0 : 1,
        pairs: (props.crypto + '_' + COIN_TRADE)?.toLocaleUpperCase(),
      }).then(res => {
        if (Number(res?.resMsg?.code) === 0) {
          if (priceTab === HOME_PAGE_URL.Favorites) {
            ref?.current?.setData(data => ({
              ...data,
              list: data.list?.filter(item => item.crypto !== props.crypto),
            }))
            return
          }

          ref?.current?.setData(data => ({
            ...data,
            list: data?.list?.map(item => ({
              ...item,
              isFavorite: item?.crypto === props?.crypto ? (props.isFavorite === 1 ? 0 : 1) : item?.isFavorite,
            })),
          }))
        } else {
          res?.resMsg?.message && showNotification({ message: res?.resMsg?.message, type: 'warning' })
        }
      })
    } catch (error) {
      showNotification({ message: `${error}`, type: 'error' })
    }
  })

  // todo 新币倒计时回调
  const onTimeEnd = useMemoizedFn(() => {
    ref.current?.setData(data => {
      // todo ws 触发订阅
      handleSendMassage(
        data?.list?.map(item => `${item?.crypto?.toLocaleUpperCase()}_${COIN_TRADE}`) ?? [],
        'ticker.subscribe'
      )
      if (selectValue !== 'utc') {
        handleSendMassage([selectValue ?? 'utc'])
      }

      const oldList = data?.list?.filter(item => dayjs().isAfter(dayjs(item?.time * 1000)))
      const newList = data?.list?.filter(item => !dayjs().isAfter(dayjs(item?.time * 1000))) ?? []

      return {
        ...data,
        list: newList?.concat(oldList ?? []),
      }
    })
  })

  // todo tab 切换改变table数据
  const onChangeTab = useMemoizedFn((homePageUrl, tab) => {
    ref.current?.fetchData(
      tab
        ? {
            page: 1,
            pageSize: 20,
            category_id: tab,
          }
        : { page: 1, pageSize: 20 },
      homePageUrl,
      locale
    )
  })

  // todo 表格项跳转
  const itemOnClick = (item: BasePriceItem & { project_id?: string; project_name?: string }) => {
    // 埋点
    if (titleTab === HOME_PAGE_URL.Sectors) {
      eventTracker('price_sectors_click', {
        button_name: item?.category_id,
      })
    } else {
      const currentMap = {
        button_name: 'card',
        currency_name: item?.currency,
        gate_id: item?.gate_id,
        crypto: item?.crypto,
      }
      eventTracker('price_treade_click', currentMap)
    }

    let url = `${langPrefix}/trade/${item?.crypto}_${COIN_TRADE}`

    if (priceTab === HOME_PAGE_URL.FIAT) {
      url = `${langPrefix}/p2p/buy/${item?.crypto?.toUpperCase()}-${item?.fiat_name?.toUpperCase()}`
    }

    if (titleTab === HOME_PAGE_URL.Fundraising) {
      url = `${langPrefix}/price/fundraising/${item?.seo_path ?? (`${item?.project_name}`?.replaceAll(' ', '-')?.replaceAll(/[?#<>&]/g, '') + '-' + item?.project_id).toLocaleLowerCase()}`
    }

    location.href = url
  }

  const onFundraisingSelectChange = useMemoizedFn((type: string, value: string) => {
    const params: any = ref.current?.getParams()

    if (value === ' ') {
      if (type === 'amount') {
        delete params?.amount_start
        delete params?.amount_end
        ref.current?.setData((params: any) => {
          delete params?.amount_start
          delete params?.amount_end
          return params
        })
      } else {
        delete params?.[type]
        ref.current?.setData((params: any) => {
          delete params?.[type]
          return params
        })
      }
    }

    let newParams = { [type]: value }

    if (type === 'amount') {
      const map = value?.split('&')
      newParams = {
        // 使用 Math.floor 对 amount_start 向下取整
        amount_start: Math.floor(Number(bigNumberDividedBy(map?.[0], faitRateItem?.rate))) + '',
        // 使用 Math.ceil 对 amount_end 向上取整
        amount_end: Math.ceil(Number(bigNumberDividedBy(map?.[1], faitRateItem?.rate))) + '',
        // amount_start: bigNumberDividedBy(map?.[0], faitRateItem?.rate),
        // amount_end: bigNumberDividedBy(map?.[1], faitRateItem?.rate),
      }
    }

    ref.current?.fetchData(
      value === ' ' ? params : { ...params, ...newParams },
      HOME_PAGE_URL.Fundraising,
      locale ?? 'en'
    )
  })

  const { columns, sectorsColumns, fundraisingColumns } = useColumns({
    lineLoading: lineLoading ?? true,
    lineData,
    onSelectChange,
    onCollect,
    selectValue,
    isCollect,
    onTimeEnd,
    onChangeTab,
    onFundraisingSelectChange,
  })

  const onDataChangeCallback = (list: BasePriceItem[]) => {
    setList(list)
    const gate_ids = COIN_GATE_IDS(list)
    if (gate_ids) {
      getLine(gate_ids)
    }

    if (isOpen) {
      handleSendMassage(
        list?.map(item => `${item?.crypto?.toLocaleUpperCase()}_${COIN_TRADE}`) ?? [],
        'ticker.subscribe'
      )

      if (selectValue !== 'utc') {
        handleSendMassage([selectValue ?? 'utc'])
      }
    }
  }

  const tabRefs = [...Array(typeTabsList?.length)].map(() => createRef<HTMLButtonElement>())

  /**
   * @description: 当前column
   * @param {*} BaseTableColumn
   * @return {*}
   */
  const priceTableColumns = (): BaseTableColumn<any>[] => {
    if (titleTab === HOME_PAGE_URL.Fundraising) {
      return fundraisingColumns
    }
    if (titleTab === HOME_PAGE_URL.Sectors) {
      return sectorsColumns
    }
    return columns
  }

  /**
   * @description: 是否是投融资h5 ｜ 特殊处理卡片展示
   * @param {*} useMemo
   * @return {*}
   */
  const isH5Fundraising = useMemo(() => {
    return isH5 && titleTab === HOME_PAGE_URL.Fundraising
  }, [isH5, titleTab])

  const { run: onInputChange } = useDebounceFn(
    (value: string) => {
      const params: any = ref.current?.getParams()

      if (!value?.length) {
        delete params?.project_name
        ref.current?.fetchData(
          {
            ...params,
            page: 1,
            pageSize: 20,
          } as any,
          HOME_PAGE_URL.Fundraising,
          locale
        )
      } else {
        ref.current?.fetchData(
          {
            ...params,
            page: 1,
            pageSize: 20,
            project_name: value,
          } as any,
          HOME_PAGE_URL.Fundraising,
          locale
        )
      }
    },
    {
      wait: 500,
    }
  )

  return (
    <PriceTableStyled data-tab={String(titleTab === HOME_PAGE_URL.Fundraising)}>
      <TitleTabs onInputChange={onInputChange} onChangeTab={onChangeTab} />
      <div id="table" className="hash-table" />
      {titleTab === HOME_PAGE_URL.Market && <PriceTabs />}
      {titleTab === HOME_PAGE_URL.Market && [HOME_PAGE_URL.Market, HOME_PAGE_URL.FIAT].includes(priceTab) && (
        <>
          {isH5 ? (
            <ArrowScrollTabsV2 tabRefs={tabRefs}>
              <TypeTabs onChangeTab={onChangeTab} tabRefs={tabRefs} />
            </ArrowScrollTabsV2>
          ) : (
            <TypeTabs onChangeTab={onChangeTab} tabRefs={tabRefs} />
          )}
        </>
      )}

      <BaseTable
        isCustomTable
        tableKey={titleTab === HOME_PAGE_URL.Fundraising ? 'fundraising-table' : undefined}
        ref={ref}
        data={priceList}
        defaultArgs={[titleTab === HOME_PAGE_URL.Sectors ? HOME_PAGE_URL.Sectors : priceTab, locale]}
        className="price-table"
        columns={priceTableColumns()}
        CustomCardTable={isH5Fundraising ? FundingListCard : null}
        request={getPriceList as any}
        onDataChangeCallback={titleTab === HOME_PAGE_URL?.Fundraising ? undefined : onDataChangeCallback}
        itemOnClick={itemOnClick as any}
      />
    </PriceTableStyled>
  )
}

export default observer(PriceTable)
